/**
 * bathymetryField.ts — Convert a parametric basin description into a 2-D depth
 *                      raster suitable for rendering and for placement scoring.
 *
 * Algorithm:
 *   1. Build a regular N×N grid covering the bounding box.
 *   2. Each cell is INSIDE the basin if the polygon outline contains it.
 *   3. For INSIDE cells, compute the radial distance r from the deepest point,
 *      normalised by the largest such distance r_max within the polygon.
 *   4. Depth(cell) = depth_max · (1 − r/r_max)^p
 *   5. Cells outside the polygon receive depth = NaN ("land").
 */

import type { SiteBathymetry } from '@/lib/benchmarkSites';

export interface DepthRaster {
  /** N×N depth values; NaN outside polygon. */
  depths: number[][];
  /** N — grid resolution. */
  N: number;
  /** Maximum modelled depth in metres. */
  depthMax: number;
  /** Cells inside the polygon. */
  insideCount: number;
  /** Threshold depth that delimits the deepest 30 % of cells (deep-zone mask). */
  deepThreshold: number;
}

/** Ray-casting point-in-polygon. */
function pointInPolygon(x: number, y: number, poly: Array<[number, number]>) {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const xi = poly[i][0], yi = poly[i][1];
    const xj = poly[j][0], yj = poly[j][1];
    const intersect = ((yi > y) !== (yj > y)) && (x < ((xj - xi) * (y - yi)) / (yj - yi + 1e-12) + xi);
    if (intersect) inside = !inside;
  }
  return inside;
}

export function buildDepthRaster(b: SiteBathymetry, depthMax: number): DepthRaster {
  const N = b.gridN ?? 40;
  const depths: number[][] = Array.from({ length: N }, () => new Array(N).fill(NaN));
  const [dx, dy] = b.deepest;

  // First pass — compute r_max within polygon
  let rMax = 0;
  const inside: boolean[][] = Array.from({ length: N }, () => new Array(N).fill(false));
  for (let j = 0; j < N; j++) {
    for (let i = 0; i < N; i++) {
      const x = (i + 0.5) / N;
      const y = (j + 0.5) / N;
      if (pointInPolygon(x, y, b.outline)) {
        inside[j][i] = true;
        const r = Math.hypot(x - dx, y - dy);
        if (r > rMax) rMax = r;
      }
    }
  }
  if (rMax === 0) rMax = 0.5;

  // Second pass — depths
  let insideCount = 0;
  const allDepths: number[] = [];
  for (let j = 0; j < N; j++) {
    for (let i = 0; i < N; i++) {
      if (!inside[j][i]) continue;
      const x = (i + 0.5) / N;
      const y = (j + 0.5) / N;
      const r = Math.hypot(x - dx, y - dy);
      const norm = Math.min(1, r / rMax);
      const d = depthMax * Math.pow(1 - norm, b.hypsometricP);
      depths[j][i] = d;
      insideCount++;
      allDepths.push(d);
    }
  }

  // Deep-zone threshold = 70th-percentile depth (deepest 30 %)
  allDepths.sort((a, c) => a - c);
  const deepThreshold = allDepths[Math.floor(allDepths.length * 0.7)] ?? depthMax * 0.7;

  return { depths, N, depthMax, insideCount, deepThreshold };
}

/** Returns viridis-ish colour for a given depth (m) given max depth. */
export function depthToColour(depth: number, depthMax: number): string {
  if (!isFinite(depth)) return '#0a0f1a';   // land
  const t = Math.max(0, Math.min(1, depth / depthMax));
  // shallow = cyan, deep = navy
  const r = Math.round(40 + (1 - t) * 100);
  const g = Math.round(80 + (1 - t) * 130);
  const b = Math.round(120 + (1 - t) * 130);
  return `rgb(${r},${g},${b})`;
}

/** Convert raster cell (i, j) to physical metres given the basin extent. */
export function cellToMetres(i: number, j: number, N: number, extent: { width: number; height: number }) {
  return {
    x_m: ((i + 0.5) / N) * extent.width,
    y_m: ((j + 0.5) / N) * extent.height,
  };
}

/** Average depth in metres over the inside cells. */
export function meanDepth(raster: DepthRaster): number {
  let s = 0, n = 0;
  for (let j = 0; j < raster.N; j++) {
    for (let i = 0; i < raster.N; i++) {
      if (isFinite(raster.depths[j][i])) {
        s += raster.depths[j][i];
        n++;
      }
    }
  }
  return n > 0 ? s / n : 0;
}
